import { useEffect } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout from "./ModalBasicLayout";
import TextField from "../Form/TextField";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import {
  BUTTON_CANCEL,
  BUTTON_SAVE,
  ERROR,
  FIELD_REQUIRED,
  GALLERY_ASSETS_NAME_LABEL,
  GALLERY_ASSETS_RENAME_ERROR,
  GALLERY_ASSETS_RENAME_SUCCESS,
  GALLERY_ASSETS_RENAME_TITLE,
} from "../../i18n/keysTranslations";
import {
  getAssetDisplayName,
  stripAssetFileExtension,
} from "../../utils/assetGalleryUtils";

const ModalRenameAsset = ({
  modalOpen = false,
  asset = null,
  isLoading = false,
  onCloseModal = () => {},
  onCallbackRename = async () => ({ ok: false }),
}) => {
  const { t } = useTranslationApp();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
    },
  });

  const initialName = asset ? getAssetDisplayName({ item: asset }) : "";

  useEffect(() => {
    if (modalOpen) {
      reset({ name: initialName });
    }
  }, [initialName, modalOpen, reset]);

  const onSubmit = async ({ name }) => {
    const trimmedName = stripAssetFileExtension({ name });

    if (!trimmedName) return;

    const response = await onCallbackRename({
      contentID: asset?.id,
      name: trimmedName,
    });

    if (!response?.ok) {
      await SimpleAlert({
        icon: "error",
        title: t(ERROR),
        text: t(GALLERY_ASSETS_RENAME_ERROR),
      });
      return;
    }

    await SimpleAlert({
      icon: "success",
      title: t(GALLERY_ASSETS_RENAME_SUCCESS),
    });
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(GALLERY_ASSETS_RENAME_TITLE)}
      ariaLabel={t(GALLERY_ASSETS_RENAME_TITLE)}
      maxWidth="sm"
      fullWidth
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <TextField
          name="name"
          control={control}
          errors={errors}
          label={t(GALLERY_ASSETS_NAME_LABEL)}
          variant="filled"
          fullWidth
          autoFocus
          disabled={isLoading}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => value?.trim().length > 0 || t(FIELD_REQUIRED),
          }}
        />
      </Box>
      <DialogActions
        sx={{
          px: {
            xs: 3,
            sm: 5,
          },
          pb: 3,
          mt: 0.5,
        }}
      >
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
            disabled: isLoading,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            onClick: handleSubmit(onSubmit),
            loading: isLoading,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalRenameAsset;
